import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { InventoryService } from '../../../core/services/inventory.service';
import { S3FileService } from '../../../core/services/s3-file.service';

export interface ProductItemOption {
  id: string;
  reference_number: string;
  product_variant_id: string;
  main_image: string | null;
  status: string;
  attribute_values: Record<string, unknown>;
}

@Component({
  selector: 'app-product-item-picker',
  standalone: false,
  templateUrl: './product-item-picker.html',
  styleUrl: './product-item-picker.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ProductItemPicker), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => ProductItemPicker), multi: true },
  ],
})
export class ProductItemPicker implements ControlValueAccessor, Validator, OnChanges {
  @Input() productId: string | null = null;
  @Input() productVariantId: string | null = null;
  @Input() locationId: string | null = null;
  @Input() placeholder = 'Select an individual item';
  @Input() allowedStatuses: string[] = ['available'];
  @Output() itemSelected = new EventEmitter<ProductItemOption | null>();
  items: ProductItemOption[] = [];
  search = '';
  value = '';
  disabled = false;
  open = false;
  loading = false;
  loadError = '';
  private loadVersion = 0;
  private onValidatorChange: () => void = () => undefined;
  imageUrls = new Map<string, string>();
  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private readonly inventory: InventoryService, private readonly s3Files: S3FileService) {}

  ngOnChanges(changes: SimpleChanges): void {
    const productChange = changes['productId'];
    if (productChange) {
      if (productChange.previousValue && productChange.previousValue !== productChange.currentValue) this.clear();
      void this.load();
    }
    const variantChange = changes['productVariantId'];
    if (variantChange && !variantChange.firstChange && variantChange.previousValue !== variantChange.currentValue) {
      if (this.selected && this.selected.product_variant_id !== variantChange.currentValue) this.clear();
    }
    const locationChange = changes['locationId'];
    if (locationChange && !locationChange.firstChange && locationChange.previousValue !== locationChange.currentValue) {
      this.clear();
      void this.load();
    }
  }

  get selected(): ProductItemOption | null { return this.items.find((item) => item.id === this.value) || null; }
  get filteredItems(): ProductItemOption[] {
    const query = this.search.trim().toLowerCase();
    return this.items.filter((item) => this.allowedStatuses.includes(item.status)
      && (!this.productVariantId || item.product_variant_id === this.productVariantId) && (!query ||
      item.reference_number.toLowerCase().includes(query) || JSON.stringify(item.attribute_values).toLowerCase().includes(query)));
  }

  async load(): Promise<void> {
    const version = ++this.loadVersion;
    this.items = [];
    this.loadError = '';
    this.imageUrls.clear();
    if (!this.productId || !this.locationId) { this.loading = false; this.onValidatorChange(); return; }
    this.loading = true;
    try {
      const items = await this.inventory.getProductItems(this.productId, this.locationId);
      if (version !== this.loadVersion) return;
      this.items = items;
      await Promise.all(this.items.map(async (item) => {
        if (!item.main_image) return;
        // An optional thumbnail failure must not invalidate a loaded canonical item.
        try {
          const url = await firstValueFrom(this.s3Files.getFileUrlValue(item.main_image));
          if (url && version === this.loadVersion) this.imageUrls.set(item.id, url);
        } catch { /* Keep the item label and existing placeholder image. */ }
      }));
    }
    catch {
      if (version === this.loadVersion) this.loadError = 'Unable to load individual items. Retry before saving.';
    }
    finally {
      if (version === this.loadVersion) { this.loading = false; this.onValidatorChange(); }
    }
  }

  imageUrl(item: ProductItemOption): string { return this.imageUrls.get(item.id) ?? ''; }

  show(): void {
    if (!this.disabled && this.productId && this.locationId) {
      this.open = true;
      void this.load(); // Refresh stock status when an existing draft is reopened.
    }
  }
  close(): void { this.open = false; this.onTouched(); }
  choose(item: ProductItemOption): void {
    if (this.disabled || !this.filteredItems.some(option => option.id === item.id)) return;
    this.value = item.id; this.onChange(item.id); this.itemSelected.emit(item); this.close();
  }
  clear(): void { this.value = ''; this.onChange(null); this.itemSelected.emit(null); }
  writeValue(value: string | null): void { this.value = value || ''; this.onValidatorChange(); }
  registerOnChange(fn: (value: string | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.value) return null;
    if (this.loading) return { productItemLoading: true };
    if (this.loadError) return { productItemUnavailable: this.loadError };
    const item = this.selected;
    return item && (!this.productVariantId || item.product_variant_id === this.productVariantId)
      ? null : { productItemUnavailable: 'The saved individual item does not match this product and variant.' };
  }
  registerOnValidatorChange(fn: () => void): void { this.onValidatorChange = fn; }

}
